import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDivisions } from '../../hooks/useDivisions'
import { usePoules } from '../../hooks/usePoules'
import { useTeams } from '../../hooks/useTeams'
import { Card, CardHeader, Button, Input, Modal, Badge, DarkModeToggleCompact } from '../../components/ui'
import { Grid3x3, Trophy, Plus, Trash2, ArrowLeft, Layout, Users, Sparkles, CheckCircle2, ArrowRight } from 'lucide-react'
import { PouleIndeling } from '../../components/features/PouleIndeling'
import { HelpTip } from '../../components/ui/HelpTip'

import { useTranslation } from 'react-i18next'

export const DivisionsPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { tournamentId } = useParams()
  const { divisions, loading: divLoading, createDivision, deleteDivision } = useDivisions(tournamentId)
  const { poules, loading: pouleLoading, createPoule, deletePoule } = usePoules(tournamentId)
  const { teams, setTeams, loading: teamLoading } = useTeams(tournamentId)

  const [activeTab, setActiveTab] = useState('manage')
  const [showDivModal, setShowDivModal] = useState(false)
  const [showPouleModal, setShowPouleModal] = useState(false)
  const [newDivision, setNewDivision] = useState({ name: '' })
  const [newPoule, setNewPoule] = useState({ name: '', division_id: '' })

  // Issue #7: Auto-switch to assignment tab when teams need to be assigned
  const [tabInitialized, setTabInitialized] = useState(false)
  useEffect(() => {
    if (tabInitialized) return
    if (poules.length > 0 && teams.length > 0) {
      const unassigned = teams.filter(t => !t.poule_id)
      if (unassigned.length > 0) {
        setActiveTab('assign')
      }
      setTabInitialized(true)
    }
  }, [poules, teams, tabInitialized])

  const [showQuickPouleModal, setShowQuickPouleModal] = useState(false)
  const [quickPouleCount, setQuickPouleCount] = useState(4)
  const [quickPouleNaming, setQuickPouleNaming] = useState('alpha') // alpha (A,B,C) or numeric (1,2,3)

  const handleCreateDivision = async (e) => {
    e.preventDefault()
    try {
      await createDivision(newDivision)
      setShowDivModal(false)
      setNewDivision({ name: '' })
    } catch (err) {
      alert(t('common.error') + ': ' + err.message)
    }
  }

  const handleCreatePoule = async (e) => {
    e.preventDefault()
    try {
      let pouleData = { ...newPoule }
      if (!pouleData.division_id && divisions.length === 0) {
        const div = await createDivision({ name: t('divisions_page.division_general') })
        pouleData.division_id = div.id
      }
      if (!pouleData.division_id && divisions.length > 0) {
        // Auto-select first division if none selected but exists
        pouleData.division_id = divisions[0].id
      }
      if (!pouleData.division_id) throw new Error(t('divisions_page.error_select_division'))

      await createPoule(pouleData)
      setShowPouleModal(false)
      setNewPoule({ name: '', division_id: '' })
    } catch (err) {
      alert(t('common.error') + ': ' + err.message)
    }
  }

  const handleQuickGeneratePoules = async () => {
    try {
      let divId
      if (divisions.length === 0) {
        const div = await createDivision({ name: t('divisions_page.division_general') })
        divId = div.id
      } else {
        divId = divisions[0].id
      }

      const promises = []
      for (let i = 0; i < quickPouleCount; i++) {
        let name = ''
        if (quickPouleNaming === 'alpha') {
          name = `${t('divisions_page.poule_name_prefix')} ${String.fromCharCode(65 + i)}` // A, B, C...
        } else {
          name = `${t('divisions_page.poule_name_prefix')} ${i + 1}`
        }
        // Issue #10: Skip if poule name already exists
        const alreadyExists = poules.some(p => p.name === name)
        if (!alreadyExists) {
          promises.push(createPoule({ name, division_id: divId }))
        }
      }

      if (promises.length === 0) {
        alert(t('divisions_page.quick_generate_exists_error'))
        setShowQuickPouleModal(false)
        return
      }

      await Promise.all(promises)
      setShowQuickPouleModal(false)
      alert(t('divisions_page.quick_generate_success', { count: promises.length }))
    } catch (err) {
      alert(t('common.error') + ': ' + err.message)
    }
  }

  return (
    <div className="min-h-screen">
      <div className="premium-container">
        {/* Hero Section */}
        <div className="mb-12 animate-slideUp">
          <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} className="mb-4">
            <ArrowLeft size={16} className="mr-2" />
            {t('divisions_page.back_to_dashboard')}
          </Button>

          <div className="flex justify-between items-start mb-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-2xl bg-gradient-to-br from-blue-400 to-blue-600 shadow-lg">
                <Grid3x3 className="text-white" size={32} />
              </div>
              <div>
                <h1 className="text-5xl font-bold text-gray-900 leading-tight">
                  {t('divisions_page.title')}
                </h1>
                <p className="text-lg text-gray-600 mt-1">
                  {t('divisions_page.subtitle')}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <DarkModeToggleCompact />

              {/* Tab Switches */}
              <div className="flex glass-strong p-1 rounded-xl border border-white/30 shadow-sm">
                <button
                  onClick={() => setActiveTab('manage')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${activeTab === 'manage'
                    ? 'bg-[var(--accent-primary)] text-white shadow-md'
                    : 'text-gray-600 hover:bg-white/20'
                    }`}
                >
                  <Layout size={18} />
                  <span className="hidden sm:inline">{t('divisions_page.tab_structure')}</span>
                </button>
                <button
                  onClick={() => setActiveTab('assign')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${activeTab === 'assign'
                    ? 'bg-[var(--accent-primary)] text-white shadow-md'
                    : 'text-gray-600 hover:bg-white/20'
                    }`}
                >
                  <Users size={18} />
                  <span className="hidden sm:inline">{t('divisions_page.tab_assignment')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        {activeTab === 'manage' ? (
          <>
            <HelpTip title={t('divisions_page.help_title')}>
              <span dangerouslySetInnerHTML={{ __html: t('divisions_page.help_text') }} />
            </HelpTip>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Divisions Card */}
              <div className="glass-strong premium-card rounded-2xl hover-lift">
                <CardHeader
                  icon={Grid3x3}
                  title={t('divisions_page.divisions_card_title')}
                  subtitle={t('divisions_page.division_count', { count: divisions.length })}
                  iconColor="from-blue-400 to-blue-600"
                />

                <Button
                  onClick={() => setShowDivModal(true)}
                  variant="primary"
                  className="mb-6"
                  icon={<Plus size={18} />}
                >
                  {t('divisions_page.btn_new_division')}
                </Button>

                {divLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-[var(--accent-primary)]"></div>
                  </div>
                ) : divisions.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="p-4 bg-gray-100 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                      <Grid3x3 className="text-gray-400" size={40} />
                    </div>
                    <p className="text-gray-900 text-xl font-semibold mb-2">{t('divisions_page.no_divisions_title')}</p>
                    <p className="text-gray-500">{t('divisions_page.no_divisions_text')}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {divisions.map(div => (
                      <div
                        key={div.id}
                        className="group glass-subtle rounded-xl p-4 border border-white/30 hover:scale-[1.01] transition-all flex justify-between items-center"
                      >
                        <span className="font-semibold text-gray-900">{div.name}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteDivision(div.id)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50 hover:text-red-500"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Poules Card */}
              <div className="glass-strong premium-card rounded-2xl hover-lift">
                <CardHeader
                  icon={Trophy}
                  title={t('divisions_page.poules_card_title')}
                  subtitle={t('divisions_page.poule_count', { count: poules.length })}
                  iconColor="from-orange-400 to-orange-600"
                />

                <Button
                  onClick={() => setShowPouleModal(true)}
                  variant="primary"
                  className="mb-6"
                  icon={<Plus size={18} />}
                >
                  {t('divisions_page.btn_new_poule')}
                </Button>

                <Button
                  onClick={() => setShowQuickPouleModal(true)}
                  variant="secondary"
                  className="mb-6 ml-2"
                  icon={<Sparkles size={18} />}
                >
                  {t('divisions_page.btn_quick_poule')}
                </Button>

                {pouleLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-[var(--accent-primary)]"></div>
                  </div>
                ) : poules.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="p-4 bg-gray-100 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                      <Trophy className="text-gray-400" size={40} />
                    </div>
                    <p className="text-gray-900 text-xl font-semibold mb-2">{t('divisions_page.no_poules_title')}</p>
                    <p className="text-gray-500">{t('divisions_page.no_poules_text')}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {poules.map(poule => (
                      <div
                        key={poule.id}
                        className="group glass-subtle rounded-xl p-4 border border-white/30 hover:scale-[1.01] transition-all flex justify-between items-center"
                      >
                        <div>
                          <span className="font-semibold text-gray-900 block">{poule.name}</span>
                          <Badge variant="default" size="sm" className="mt-1">
                            {poule.division?.name || t('divisions_page.no_division_badge')}
                          </Badge>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deletePoule(poule.id)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50 hover:text-red-500"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Navigation Action Bar */}
            <div className="mt-8 glass-strong rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-4 border border-[var(--accent-primary)]/20 shadow-lg animate-slideUp">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-full">
                  <CheckCircle2 className="text-green-600" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg">{t('divisions_page.complete_title')}</h3>
                  <p className="text-gray-600 text-sm">{t('divisions_page.complete_text')}</p>
                </div>
              </div>

              <Button
                variant="primary"
                size="lg"
                onClick={() => navigate(`/admin/tournaments/${tournamentId}/teams`)}
                className="w-full md:w-auto min-w-[200px] shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1"
                icon={<ArrowRight size={20} />}
              >
                {t('divisions_page.btn_next_step')}
              </Button>
            </div>
          </>
        ) : (
          /* Assignment Tab */
          <div className="animate-fadeIn">
            <PouleIndeling
              tournamentId={tournamentId}
              divisions={divisions}
              poules={poules}
              teams={teams}
              setTeams={setTeams}
            />
          </div>
        )}
      </div>

      {/* Division Modal */}
      <Modal isOpen={showDivModal} onClose={() => setShowDivModal(false)} title={t('divisions_page.modal_new_division_title')} size="md">
        <form onSubmit={handleCreateDivision} className="space-y-6">
          <Input
            label={t('divisions_page.label_division_name')}
            value={newDivision.name}
            onChange={(e) => setNewDivision({ name: e.target.value })}
            placeholder={t('divisions_page.placeholder_division_name')}
            icon={<Grid3x3 size={18} />}
            required
          />
          <Button type="submit" variant="primary" size="lg" className="w-full" icon={<Plus size={20} />}>
            {t('divisions_page.btn_create_division')}
          </Button>
        </form>
      </Modal>

      {/* Poule Modal */}
      <Modal isOpen={showPouleModal} onClose={() => setShowPouleModal(false)} title={t('divisions_page.modal_new_poule_title')} size="md">
        <form onSubmit={handleCreatePoule} className="space-y-6">
          <Input
            label={t('divisions_page.label_poule_name')}
            value={newPoule.name}
            onChange={(e) => setNewPoule({ ...newPoule, name: e.target.value })}
            placeholder={t('divisions_page.placeholder_poule_name')}
            icon={<Trophy size={18} />}
            required
          />

          <div>
            <label className="label-premium mb-3 block">{t('divisions_page.label_division')}</label>
            {divisions.length > 0 ? (
              <select
                className="w-full px-4 py-3 rounded-xl border-2 border-white/30 glass-subtle focus:border-[var(--accent-primary)] focus:ring-2 focus:ring-[var(--accent-primary)]/20 outline-none transition-all text-gray-900"
                value={newPoule.division_id}
                onChange={(e) => setNewPoule({ ...newPoule, division_id: e.target.value })}
                required
              >
                <option value="">{t('divisions_page.select_division_placeholder')}</option>
                {divisions.map(div => (
                  <option key={div.id} value={div.id}>{div.name}</option>
                ))}
              </select>
            ) : (
              <div className="glass-subtle border-2 border-blue-500/30 rounded-xl p-4 flex items-start gap-3">
                <Sparkles className="text-blue-500 flex-shrink-0" size={20} />
                <p className="text-sm text-blue-600 font-medium">
                  {t('divisions_page.auto_division_hint')}
                </p>
              </div>
            )}
          </div>

          <Button type="submit" variant="primary" size="lg" className="w-full" icon={<Plus size={20} />}>
            {t('divisions_page.btn_create_poule')}
          </Button>
        </form>
      </Modal>

      {/* Quick Poule Modal */}
      <Modal isOpen={showQuickPouleModal} onClose={() => setShowQuickPouleModal(false)} title={t('divisions_page.modal_quick_poule_title')} size="md">
        <div className="space-y-6">
          <p className="text-gray-600">{t('divisions_page.quick_poule_desc')}</p>

          <div>
            <label className="label-premium mb-2 block">{t('divisions_page.label_quick_poule_count')}</label>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="2" max="16"
                value={quickPouleCount}
                onChange={(e) => setQuickPouleCount(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <span className="text-xl font-bold w-12 text-center">{quickPouleCount}</span>
            </div>
          </div>

          <div>
            <label className="label-premium mb-2 block">{t('divisions_page.label_quick_poule_naming')}</label>
            <div className="flex gap-2">
              <button
                onClick={() => setQuickPouleNaming('alpha')}
                className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-all ${quickPouleNaming === 'alpha' ? 'bg-blue-50 border-blue-500 text-blue-700' : 'border-gray-200 hover:bg-gray-50'}`}
              >
                {t('divisions_page.naming_alpha')}
              </button>
              <button
                onClick={() => setQuickPouleNaming('numeric')}
                className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-all ${quickPouleNaming === 'numeric' ? 'bg-blue-50 border-blue-500 text-blue-700' : 'border-gray-200 hover:bg-gray-50'}`}
              >
                {t('divisions_page.naming_numeric')}
              </button>
            </div>
          </div>

          <Button onClick={handleQuickGeneratePoules} variant="primary" size="lg" className="w-full" icon={<Sparkles size={20} />}>
            {t('divisions_page.btn_quick_generate', { count: quickPouleCount })}
          </Button>
        </div>
      </Modal>
    </div >
  )
}
